import React, { useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import { fmt, dateFmt } from "../helpers/format"
import {
  candidatePurchaseInvoices,
  linkPurchaseInvoice,
  unlinkPurchaseInvoice,
} from "../api/subcontracts"

const SubcontractPurchaseInvoicesModal = ({ projectId, subcontract, onClose, onChange }) => {
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading]       = useState(true)
  const [query, setQuery]           = useState("")

  useEffect(() => {
    if (!subcontract) return
    setLoading(true)
    candidatePurchaseInvoices(projectId, subcontract.id).then(response => {
      setCandidates(Array.isArray(response) ? response : [])
      setLoading(false)
    })
  }, [projectId, subcontract])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return candidates
    return candidates.filter(pi => (pi.title || "").toLowerCase().includes(q))
  }, [candidates, query])

  if (!subcontract) return null

  const handleLink = (pi) => {
    linkPurchaseInvoice(projectId, subcontract.id, pi.id).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      onChange(response)
      setCandidates(prev => prev.filter(c => c.id !== pi.id))
      flashNotice(I18n.t("projects.subcontracts.purchase_invoices.linked"))
    })
  }

  const handleUnlink = (pi) => {
    unlinkPurchaseInvoice(projectId, subcontract.id, pi.id).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      onChange(response)
      setCandidates(prev => [{ id: pi.id, title: pi.title, status: pi.status, date: pi.date, subtotal: pi.subtotal }, ...prev])
    })
  }

  const linked = subcontract.purchase_invoices || []

  return (
    <Modal isOpen={true} onClose={onClose} title={I18n.t("projects.subcontracts.purchase_invoices.title", { number: subcontract.number })} size="lg">
      <div className="px-5 py-4 space-y-5">
        <div className="text-[12.5px] text-slate-600 flex flex-wrap gap-3">
          <span>{I18n.t("projects.subcontracts.purchase_invoices.contract")} <strong className="text-slate-900">{fmt(subcontract.contract_amount)}</strong></span>
          <span>{I18n.t("projects.subcontracts.purchase_invoices.consumed")} <strong className="text-amber-700">{fmt(subcontract.consumed_amount)}</strong> ({subcontract.consumed_pct}%)</span>
          <span>{I18n.t("projects.subcontracts.purchase_invoices.budget_remaining")} <strong className="text-slate-900">{fmt(subcontract.budget_remaining)}</strong></span>
        </div>

        {linked.length > 0 && (
          <section>
            <h3 className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium mb-2">
              {I18n.t("projects.subcontracts.purchase_invoices.linked_heading")}
            </h3>
            <ul className="border border-slate-200 rounded-md divide-y divide-slate-100">
              {linked.map(pi => (
                <li key={pi.id} className="px-3 py-2 flex items-center gap-3">
                  <FontAwesomeIcon icon="file-invoice" className="text-slate-400" />
                  <a href={pi.path} target="_blank" rel="noopener noreferrer" className="flex-1 text-[12.5px] text-slate-900 hover:text-blue-700 truncate">
                    {pi.title || I18n.t("projects.subcontracts.purchase_invoices.untitled", { id: pi.id })}
                  </a>
                  <span className="text-[11.5px] text-slate-500 tabular-nums">{dateFmt(pi.date)}</span>
                  <span className="text-[12.5px] font-semibold text-slate-900 tabular-nums">{fmt(pi.subtotal)}</span>
                  <button
                    type      = "button"
                    onClick   = {() => handleUnlink(pi)}
                    title     = {I18n.t("projects.subcontracts.purchase_invoices.unlink")}
                    className = "text-rose-600 hover:text-rose-800 text-[11.5px] px-2"
                  >
                    <FontAwesomeIcon icon="link-slash" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <h3 className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium mb-2">
            {I18n.t("projects.subcontracts.purchase_invoices.candidates_heading", { supplier: subcontract.supplier?.name })}
          </h3>
          <input
            type        = "search"
            value       = {query}
            onChange    = {e => setQuery(e.target.value)}
            placeholder = {I18n.t("projects.subcontracts.purchase_invoices.search_placeholder")}
            className   = "w-full h-9 px-3 border border-slate-300 rounded text-[13px] mb-2"
          />
          {loading ? (
            <div className="py-6 text-center text-[12.5px] text-slate-500">
              <FontAwesomeIcon icon="spinner" spin />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-6 text-center text-[12.5px] text-slate-500">
              {I18n.t("projects.subcontracts.purchase_invoices.candidates_empty")}
            </div>
          ) : (
            <ul className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {filtered.map(pi => (
                <li key={pi.id} className="px-3 py-2 flex items-center gap-3 hover:bg-slate-50">
                  <FontAwesomeIcon icon="file-invoice" className="text-slate-400" />
                  <div className="flex-1 text-[12.5px] text-slate-900 truncate">
                    {pi.title || I18n.t("projects.subcontracts.purchase_invoices.untitled", { id: pi.id })}
                  </div>
                  <span className="text-[11.5px] text-slate-500 tabular-nums">{dateFmt(pi.date)}</span>
                  <span className="text-[12.5px] font-semibold text-slate-900 tabular-nums">{pi.subtotal?.formatted}</span>
                  <button
                    type      = "button"
                    onClick   = {() => handleLink(pi)}
                    className = "px-2 py-1 text-[11.5px] text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded inline-flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon="link" /> {I18n.t("projects.subcontracts.purchase_invoices.link")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
        <Button variant="ghost" onClick={onClose}>{I18n.t("button.close")}</Button>
      </div>
    </Modal>
  )
}

export default SubcontractPurchaseInvoicesModal
